/* Presentational building blocks shared by the two absolute-item render paths
   (header zone + page) in LivePreview, plus small pure helpers. Mixed exports,
   so fast refresh's component-only rule doesn't apply here. */
/* eslint-disable react-refresh/only-export-components */
import {type ResizeHandle} from '../hooks/useCanvasDrag';

interface HandleDef {
  id: ResizeHandle;
  pos: React.CSSProperties;
  size: [number, number];
  cursor: string;
  corner: boolean;
}

const HANDLE_DEFS: HandleDef[] = [
  {id: 'nw', pos: {top: -5, left: -5}, size: [10, 10], cursor: 'nwse-resize', corner: true},
  {
    id: 'n',
    pos: {top: -5, left: '50%', transform: 'translateX(-50%)'},
    size: [20, 8],
    cursor: 'ns-resize',
    corner: false,
  },
  {id: 'ne', pos: {top: -5, right: -5}, size: [10, 10], cursor: 'nesw-resize', corner: true},
  {
    id: 'w',
    pos: {top: '50%', left: -5, transform: 'translateY(-50%)'},
    size: [8, 20],
    cursor: 'ew-resize',
    corner: false,
  },
  {
    id: 'e',
    pos: {top: '50%', right: -5, transform: 'translateY(-50%)'},
    size: [8, 20],
    cursor: 'ew-resize',
    corner: false,
  },
  {id: 'sw', pos: {bottom: -5, left: -5}, size: [10, 10], cursor: 'nesw-resize', corner: true},
  {
    id: 's',
    pos: {bottom: -5, left: '50%', transform: 'translateX(-50%)'},
    size: [20, 8],
    cursor: 'ns-resize',
    corner: false,
  },
  {id: 'se', pos: {bottom: -5, right: -5}, size: [10, 10], cursor: 'nwse-resize', corner: true},
];

/** Insertion line shown between flow elements during a reorder drag. */
export function FlowDropIndicator() {
  return (
    <div className="relative h-0.5 my-0.5 rounded-full bg-indigo-500" aria-hidden>
      <span className="absolute -left-1 top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-indigo-500" />
    </div>
  );
}

/** Selection/hover frame border for an absolute element. */
export function frameBorderFor(opts: {
  inGroupSelectionMode: boolean;
  active: boolean;
  isSelected: boolean;
  hovered: boolean;
}): string | undefined {
  if (opts.inGroupSelectionMode) return '2px dashed #6366f1';
  if (opts.active || opts.isSelected) return '2px solid #6366f1';
  if (opts.hovered) return '1.5px dashed #a5b4fc';
  return undefined;
}

/** The eight resize handles drawn around a selected absolute element. Renders
 *  nothing when `show` is false. */
export function ResizeHandles({
  show,
  onResizeStart,
}: {
  show: boolean;
  onResizeStart: (e: React.MouseEvent, handle: ResizeHandle) => void;
}) {
  if (!show) return null;
  return (
    <>
      {HANDLE_DEFS.map((h) => (
        <div
          key={h.id}
          onMouseDown={(e) => onResizeStart(e, h.id)}
          style={{
            position: 'absolute',
            ...h.pos,
            width: h.size[0],
            height: h.size[1],
            backgroundColor: 'white',
            border: '1.5px solid #6366f1',
            borderRadius: h.corner ? 9999 : 4,
            boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
            cursor: h.cursor,
            zIndex: 10,
          }}
        />
      ))}
    </>
  );
}
